import React, { Component, createRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import ScrollBooster from 'scrollbooster';
import { ScrollArea, StyledScrollBooster } from './Carousel.styles';
import Card from '../Card/index';
import PropTypes from 'prop-types';

export default class Carousel extends Component {
  slider = createRef();

  constructor() {
    super();
    this.isPointerUp = false;
    this.prevScrollX = 0;
    this.state = { boostScroll: false };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
    }
  }

  componentDidMount() {
    if (
      !this.props.theme.fullScreen &&
      !this.props.isLargeScreen &&
      (this.state.boostScroll || this.shouldShowScroll())
    ) {
      this.initiateScrollBooster();
    }
    this.slider.current.children[0].setAttribute('dir', this.props.theme.dir);
    const tilesSeen = this.getTilesSeen();
    if (tilesSeen.length) {
      this.props.onSeen(tilesSeen, this.props.isInitialLoad);
    }
  }

  componentDidUpdate() {
    if (!this.props.theme.fullScreen && !this.props.isLargeScreen && this.shouldShowScroll()) {
      if (this.scrollBooster) {
        if (this.props.theme.dir === 'rtl' && this.props.tiles.length === 1) {
          this.slider.current.children[0].style.borderWidth = 0;
        }
        setTimeout(() => {
          if (this.scrollBooster) {
            this.scrollBooster.updateMetrics();
          }
        }, 500);
      } else {
        this.initiateScrollBooster();
      }
    } else if (this.scrollBooster) {
      this.slider.current.children[0].style.transform = 'initial';
      this.scrollBooster.updateMetrics();
      this.scrollBooster.destroy();
      this.scrollBooster = null;
      this.slider.current.removeAttribute('data-boosted');
    }
    const tilesSeen = this.getTilesSeen();
    if (tilesSeen.length) {
      this.props.onSeen(tilesSeen, false);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  getTilesSeen() {
    const seenTiles = [];
    if (this.slider && this.slider.current) {
      const sliderBounds = this.slider.current.getBoundingClientRect();
      Array.from(this.slider.current.children[0].children).forEach((card, index) => {
        const cardBounds = card.getBoundingClientRect();
        if (!this.props.theme.fullScreen) {
          if (cardBounds.left >= sliderBounds.left && cardBounds.right <= sliderBounds.right) {
            seenTiles.push(this.props.tiles[index]);
          }
        } else {
          if (cardBounds.top >= sliderBounds.top && cardBounds.bottom <= sliderBounds.bottom) {
            seenTiles.push(this.props.tiles[index]);
          }
        }
      });
    } else {
      seenTiles.push(...this.props.tiles);
    }

    return seenTiles.filter(t => t && !t.seen);
  }

  onTileClose(evt, tile) {
    evt.preventDefault();
    evt.stopPropagation();
    this.props.onClose(tile, () => {
      this.trackTilesSeen();
    });
  }

  trackTilesSeen = () => {
    const tilesSeen = this.getTilesSeen();
    this.props.onSeen(tilesSeen);
  };

  initiateScrollBooster = () => {
    this.slider.current.setAttribute('data-boosted', true);
    this.scrollBooster = new ScrollBooster({
      scrollMode: 'transform',
      direction: 'horizontal',
      viewport: this.slider.current,
      lockScrollOnDragDirection: 'horizontal',
      dragDirectionTolerance: 20,
      onUpdate: state => {
        let scrollDistance = Math.abs(this.prevScrollX - state.position.x);
        this.prevScrollX = state.position.x;
        // scrolling takes couple of seconds to completely come to a stop.
        // therefore checking tiles seen, when scrolling has slowed down,
        // rather than waiting for scrolling to stop
        if (this.isPointerUp && scrollDistance < 2 && scrollDistance > 1) {
          this.trackTilesSeen();
        }
      },
      onPointerDown: () => {
        this.isPointerUp = false;
      },
      onPointerUp: () => {
        this.trackTilesSeen();
        this.isPointerUp = true;
      }
    });
    if (this.props.theme.dir === 'rtl') {
      this.scrollBooster.setPosition({
        x: this.slider.current.children[0].clientWidth,
        y: 0
      });
    }
    //console.log(document.getElementById('carousel').children[0]);
  };

  shouldShowScroll = () => {
    const sliderWidth = this.slider.current.offsetWidth;
    const sliderContentWidth = this.slider.current.children[0].offsetWidth;
    return sliderWidth < sliderContentWidth;
  };

  handleResize = () => {
    if (!this.props.theme.fullScreen && !this.props.isLargeScreen && this.shouldShowScroll()) {
      this.setState({ boostScroll: true });
    } else if (this.scrollBooster) {
      this.setState({ boostScroll: false });
    }
  };

  render() {
    return (
      <ScrollArea id='carousel' ref={this.slider} onScroll={this.trackTilesSeen}>
        <StyledScrollBooster role='group'>
          <TransitionGroup component={null}>
            {this.props.tiles.map(tile => {
              const { engagementId, image, title, description, linkText, dismissable, isLoading } = tile;
              return (
                <CSSTransition key={engagementId} timeout={500} classNames='carousel' exit={false}>
                  <Card
                    id={engagementId}
                    backgroundImage={image}
                    title={title}
                    subtitle={description}
                    link={linkText}
                    onCardClick={() => {
                      this.props.onCardClick(tile);
                    }}
                    onClose={e => this.onTileClose(e, tile)}
                    dismissable={dismissable}
                    transitionName='carousel'
                    isLoading={isLoading}
                  />
                </CSSTransition>
              );
            })}
          </TransitionGroup>          
        </StyledScrollBooster>
      </ScrollArea>
    );
  }
}

Carousel.propTypes = {
  /**
   * Specify if screen is larger than 992px
   */
  isLargeScreen: PropTypes.bool,
  /**
   * Tiles to show
   */
  tiles: PropTypes.array,
  /**
   * Tile Click handler
   */
  onCardClick: PropTypes.func,
  /**
   *Tile dismiss handler
   */
  onClose: PropTypes.func,
  /**
   *Tile seen handler
   */
  onSeen: PropTypes.func
};

Carousel.defaultProps = {
  isLargeScreen: false,
  tiles: [],
  theme: { fullScreen: false, dir: 'ltr' },
  onClose: () => {},
  onCardClick: () => {},
  onSeen: () => {}
};
